/**
 * Payment Analytics Events
 * Typowane helpery do emisji eventów GTM dla flow płatności
 */

// GTM DataLayer type
declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

/**
 * Push event to GTM dataLayer
 */
const pushToDataLayer = (event: Record<string, unknown>): void => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(event);
  }
};

// Event types
export interface PaymentEventBase {
  paymentId: string;
  tournamentId?: string;
  amount?: string;
  currency?: string;
}

export interface PaymentInitiatedEvent extends PaymentEventBase {
  paymentType: 'TOURNAMENT_CREATE_ENTRY' | 'TOURNAMENT_JOIN_ENTRY';
}

export interface PaymentCompletedEvent extends PaymentEventBase {
  transactionHash?: string;
}

export interface PaymentFailedEvent extends PaymentEventBase {
  errorCode?: string;
  errorMessage?: string;
}

export interface TournamentTypeDetectedEvent {
  tournamentId: string;
  type: 'FREE' | 'PAID';
  amount?: string;
  currency?: string;
}

export interface TournamentPublishedEvent {
  tournamentId: string;
  paymentId: string;
}

export interface TournamentJoinedEvent {
  tournamentId: string;
  paymentId?: string;
  amount?: string;
  currency?: string;
  status?: number;
  alreadyJoined?: boolean;
}

export interface JoinFailedEvent {
  tournamentId: string;
  paymentId: string;
  errorCode?: string;
  errorMessage?: string;
}

/**
 * Fire when tournament type is detected (after getTournamentDetails)
 */
export const trackTournamentTypeDetected = (data: TournamentTypeDetectedEvent): void => {
  pushToDataLayer({
    event: 'tournament_type_detected',
    ...data,
  });
};

/**
 * Fire when payment session is initiated
 */
export const trackPaymentInitiated = (data: PaymentInitiatedEvent): void => {
  pushToDataLayer({
    event: 'payment_initiated',
    ...data,
  });
};

/**
 * Fire when payment is confirmed on-chain
 */
export const trackPaymentConfirmed = (data: PaymentEventBase): void => {
  pushToDataLayer({
    event: 'payment_confirmed',
    ...data,
  });
};

/**
 * Fire when payment is completed (after publish)
 */
export const trackPaymentCompleted = (data: PaymentCompletedEvent): void => {
  pushToDataLayer({
    event: 'payment_completed',
    ...data,
  });
};

/**
 * Fire when payment fails
 */
export const trackPaymentFailed = (data: PaymentFailedEvent): void => {
  pushToDataLayer({
    event: 'payment_failed',
    ...data,
  });
};

/**
 * Fire when payment expires
 */
export const trackPaymentExpired = (data: PaymentEventBase): void => {
  pushToDataLayer({
    event: 'payment_expired',
    ...data,
  });
};

/**
 * Fire when tournament is successfully published
 */
export const trackTournamentPublished = (data: TournamentPublishedEvent): void => {
  pushToDataLayer({
    event: 'tournament_published',
    ...data,
  });
};

/**
 * Fire when user cancels payment
 */
export const trackPaymentCancelled = (data: PaymentEventBase): void => {
  pushToDataLayer({
    event: 'payment_cancelled',
    ...data,
  });
};

/**
 * Fire when user retries payment after failure
 */
export const trackPaymentRetry = (data: PaymentEventBase): void => {
  pushToDataLayer({
    event: 'payment_retry',
    ...data,
  });
};

/**
 * Fire when user successfully joins a tournament (after payment)
 */
export const trackTournamentJoined = (data: TournamentJoinedEvent): void => {
  pushToDataLayer({
    event: 'tournament_joined',
    ...data,
  });
};

/**
 * Fire when tournament join fails
 */
export const trackJoinFailed = (data: JoinFailedEvent): void => {
  pushToDataLayer({
    event: 'join_failed',
    ...data,
  });
};

/**
 * Payment boundary error event data
 */
export interface PaymentBoundaryErrorEvent {
  errorCode: string;
  errorMessage: string;
  componentStack: string;
  timestamp: string;
  paymentId?: string;
  tournamentId?: string;
}

/**
 * Fire when PaymentErrorBoundary catches a React render error
 * Task 20.4: Analytics integration for error boundary
 */
export const trackPaymentBoundaryError = (data: PaymentBoundaryErrorEvent): void => {
  pushToDataLayer({
    event: 'payment_boundary_error',
    ...data,
  });
};
